import React, { useState, useRef, useEffect } from "react";
import { Line, Bar, Doughnut, Radar } from "react-chartjs-2";
import article from "./StopLossContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement,
  RadialLinearScale
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement,
  RadialLinearScale
);

const StockVolatilityRiskCalculator = () => {
  const content = article.find((item) => item.id === 5);
  const [activeTab, setActiveTab] = useState("analyzer");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [analysisType, setAnalysisType] = useState("historical");
  const [notifications, setNotifications] = useState([]);
  const [calculationHistory, setCalculationHistory] = useState([]);

  const [inputs, setInputs] = useState({
    // Stock Information
    symbol: "AAPL",
    currentPrice: "150.00",
    marketCap: "2500000000000",
    sector: "Technology",
    
    // Volatility Metrics
    historicalVolatility: "25",
    impliedVolatility: "28",
    beta: "1.2",
    vix: "18.5",
    
    // Price Data
    high52Week: "182.00",
    low52Week: "124.00",
    averageTrueRange: "3.50",
    standardDeviation: "18.50",
    
    // Risk Parameters
    positionSize: "10000",
    timeHorizon: "30",
    confidenceLevel: "95",
    
    // Market Conditions
    marketRegime: "Normal",
    correlationSPY: "0.85",
    earningsDate: "2024-01-25",
    
    // Advanced Metrics
    var95: "1250",
    expectedShortfall: "1850",
    maxDrawdown: "15.5",
    sharpeRatio: "1.8"
  });

  const [volatilityAnalysis, setVolatilityAnalysis] = useState(null);
  const [riskMetrics, setRiskMetrics] = useState(null);
  const chartRef = useRef();

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "analyzer" || activeTab === "metrics") {
      calculateVolatilityRisk();
    }
  }, [inputs, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const calculateVolatilityRisk = () => {
    const {
      currentPrice,
      historicalVolatility,
      impliedVolatility,
      beta,
      vix,
      high52Week,
      low52Week,
      averageTrueRange,
      standardDeviation,
      positionSize,
      timeHorizon,
      confidenceLevel,
      marketRegime,
      correlationSPY,
      var95,
      expectedShortfall,
      maxDrawdown,
      sharpeRatio
    } = inputs;

    if (!currentPrice || !historicalVolatility) {
      return;
    }

    const price = parseFloat(currentPrice);
    const histVol = parseFloat(historicalVolatility) / 100;
    const implVol = parseFloat(impliedVolatility || historicalVolatility) / 100;
    const stockBeta = parseFloat(beta || 1.0);
    const vixLevel = parseFloat(vix || 20);
    const high52 = parseFloat(high52Week || price * 1.2);
    const low52 = parseFloat(low52Week || price * 0.8);
    const atr = parseFloat(averageTrueRange || 2.0);
    const stdDev = parseFloat(standardDeviation || 15);
    const position = parseFloat(positionSize || 10000);
    const horizon = parseInt(timeHorizon || 30);
    const confidence = parseFloat(confidenceLevel || 95) / 100;
    const correlation = parseFloat(correlationSPY || 0.7);
    const marketVar = parseFloat(var95 || 1000);
    const expectedShort = parseFloat(expectedShortfall || 1500);
    const maxDD = parseFloat(maxDrawdown || 10) / 100;
    const sharpe = parseFloat(sharpeRatio || 1.5);

    // Calculate volatility metrics
    const volatilityRatio = implVol / histVol;
    const volatilityPremium = (implVol - histVol) * 100;
    const normalizedVolatility = histVol / (vixLevel / 100);
    const volatilityZScore = (histVol - 20) / 8; // Assuming 20% average vol

    // Calculate price-based metrics
    const currentToHigh = ((high52 - price) / price) * 100;
    const currentToLow = ((price - low52) / price) * 100;
    const priceRange = high52 - low52;
    const pricePosition = (price - low52) / priceRange;
    const supportDistance = ((price - low52) / price) * 100;
    const resistanceDistance = ((high52 - price) / price) * 100;

    // Risk calculations
    const dailyVolatility = histVol / Math.sqrt(252);
    const horizonVolatility = dailyVolatility * Math.sqrt(horizon);
    
    // VaR Calculation (simplified)
    const zScore = getZScore(confidence);
    const dailyVaR = position * dailyVolatility * zScore;
    const horizonVaR = position * horizonVolatility * zScore;
    
    // Expected Shortfall (CVaR)
    const dailyES = position * dailyVolatility * (zScore / (1 - confidence));
    const horizonES = position * horizonVolatility * (zScore / (1 - confidence));

    // Beta-adjusted risk
    const betaAdjustedVolatility = dailyVolatility * stockBeta;
    const marketAdjustedVaR = dailyVaR * stockBeta;

    // ATR-based position sizing
    const atrPercent = (atr / price) * 100;
    const volatilityAdjustedPosition = position * (20 / atrPercent); // Normalize to 20% vol

    // Risk scores (0-100, higher = riskier)
    const volatilityScore = Math.min(100, (histVol / 50) * 100);
    const betaScore = Math.min(100, (stockBeta / 2) * 50);
    const drawdownScore = Math.min(100, (maxDD / 0.3) * 100);
    const varScore = Math.min(100, (dailyVaR / position) * 100 * 10);
    const overallRiskScore = (volatilityScore + betaScore + drawdownScore + varScore) / 4;

    const analysis = {
      volatility: {
        historical: histVol * 100,
        implied: implVol * 100,
        ratio: volatilityRatio,
        premium: volatilityPremium,
        normalized: normalizedVolatility,
        zScore: volatilityZScore,
        daily: dailyVolatility * 100,
        horizon: horizonVolatility * 100
      },
      price: {
        current: price,
        high52: high52,
        low52: low52,
        currentToHigh: currentToHigh,
        currentToLow: currentToLow,
        range: priceRange,
        position: pricePosition,
        supportDistance: supportDistance,
        resistanceDistance: resistanceDistance,
        atr: atr,
        atrPercent: atrPercent
      },
      risk: {
        beta: stockBeta,
        correlation: correlation,
        dailyVaR: dailyVaR,
        horizonVaR: horizonVaR,
        dailyES: dailyES,
        horizonES: horizonES,
        betaAdjustedVol: betaAdjustedVolatility * 100,
        marketAdjustedVaR: marketAdjustedVaR,
        maxDrawdown: maxDD * 100,
        sharpeRatio: sharpe
      },
      metrics: {
        volatilityScore: volatilityScore,
        betaScore: betaScore,
        drawdownScore: drawdownScore,
        varScore: varScore,
        overallRiskScore: overallRiskScore,
        riskLevel: getRiskLevel(overallRiskScore),
        positionSize: position,
        timeHorizon: horizon,
        confidenceLevel: confidence * 100
      },
      recommendations: generateRecommendations(histVol * 100, stockBeta, overallRiskScore, atrPercent)
    };

    setVolatilityAnalysis(analysis);
    calculateAdvancedRiskMetrics(analysis, position, horizon);
  };

  const calculateAdvancedRiskMetrics = (analysis, position, horizon) => {
    const {
      volatility,
      risk,
      price
    } = analysis;

    // Calculate probability distributions
    const probabilities = {
      up5Percent: calculateProbability(0.05, volatility.daily / 100, horizon),
      down5Percent: calculateProbability(-0.05, volatility.daily / 100, horizon),
      up10Percent: calculateProbability(0.10, volatility.daily / 100, horizon),
      down10Percent: calculateProbability(-0.10, volatility.daily / 100, horizon),
      breachSupport: calculateProbability(-price.supportDistance / 100, volatility.daily / 100, horizon),
      breachResistance: calculateProbability(price.resistanceDistance / 100, volatility.daily / 100, horizon)
    };

    // Stress test scenarios
    const stressTests = {
      marketCrash: position * risk.beta * -0.20, // 20% market crash
      volSpike: position * (volatility.historical / 100) * 2 * -1.5, // 150% vol spike
      blackSwan: position * -0.35, // 35% black swan
      normal: position * (volatility.historical / 100) * -1
    };

    // Risk-adjusted returns
    const riskAdjustedMetrics = {
      volatilityPerReturn: risk.sharpeRatio,
      riskRewardRatio: risk.sharpeRatio / (volatility.historical / 100),
      efficiencyScore: (risk.sharpeRatio / Math.max(volatility.historical / 100, 0.01)) * 100,
      drawdownRisk: risk.maxDrawdown / Math.max(volatility.historical, 1)
    };

    const advancedMetrics = {
      probabilities,
      stressTests,
      riskAdjustedMetrics,
      scenarioAnalysis: generateScenarios(analysis)
    };

    setRiskMetrics(advancedMetrics);
  };

  const calculateProbability = (returnThreshold, dailyVol, days) => {
    // Simplified probability calculation using normal distribution
    const z = returnThreshold / (dailyVol * Math.sqrt(days));
    return (1 - (0.5 + 0.5 * Math.erf(z / Math.sqrt(2)))) * 100;
  };

  // Approximate error function
  Math.erf = function(x) {
    // Save the sign of x
    const sign = (x >= 0) ? 1 : -1;
    x = Math.abs(x);
    
    // Constants
    const a1 =  0.254829592;
    const a2 = -0.284496736;
    const a3 =  1.421413741;
    const a4 = -1.453152027;
    const a5 =  1.061405429;
    const p  =  0.3275911;
    
    // A&S formula 7.1.26
    const t = 1.0 / (1.0 + p * x);
    const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
    
    return sign * y;
  };

  const getZScore = (confidence) => {
    const zScores = {
      0.90: 1.282,
      0.95: 1.645,
      0.99: 2.326,
      0.995: 2.576,
      0.999: 3.090
    };
    return zScores[confidence] || 1.645;
  };

  const getRiskLevel = (riskScore) => {
    if (riskScore <= 25) return { level: "Low", color: "#28A745", description: "Stable" };
    if (riskScore <= 50) return { level: "Moderate", color: "#FFC107", description: "Balanced" };
    if (riskScore <= 75) return { level: "High", color: "#FF6B6B", description: "Volatile" };
    return { level: "Extreme", color: "#E53E3E", description: "Speculative" };
  };

  const generateRecommendations = (histVol, beta, riskScore, atrPercent) => {
    const recommendations = [];

    if (histVol > 40) {
      recommendations.push({
        type: "warning",
        message: "High historical volatility detected",
        action: "Consider smaller position size or hedging strategies"
      });
    }

    if (beta > 1.5) {
      recommendations.push({
        type: "warning",
        message: "High beta stock - sensitive to market moves",
        action: "Monitor market conditions closely"
      });
    }

    if (riskScore > 70) {
      recommendations.push({
        type: "danger",
        message: "Overall risk level is high",
        action: "Reduce position size or implement stop-loss"
      });
    }

    if (atrPercent > 5) {
      recommendations.push({
        type: "info",
        message: "High daily trading range",
        action: "Use wider stop-loss to avoid whipsaws"
      });
    }

    if (histVol < 15 && beta < 0.8) {
      recommendations.push({
        type: "success",
        message: "Low volatility defensive stock",
        action: "Suitable for conservative portfolios"
      });
    }

    return recommendations;
  };

  const generateScenarios = (analysis) => {
    return [
      {
        name: "Base Case",
        probability: 60,
        return: analysis.volatility.historical * 0.5,
        risk: analysis.metrics.overallRiskScore * 0.8
      },
      {
        name: "Bull Case",
        probability: 20,
        return: analysis.volatility.historical * 1.5,
        risk: analysis.metrics.overallRiskScore * 1.2
      },
      {
        name: "Bear Case",
        probability: 15,
        return: analysis.volatility.historical * -1.2,
        risk: analysis.metrics.overallRiskScore * 1.5
      },
      {
        name: "Black Swan",
        probability: 5,
        return: analysis.volatility.historical * -3.0,
        risk: analysis.metrics.overallRiskScore * 2.0
      }
    ];
  };

  const resetCalculator = () => {
    setInputs({
      symbol: "AAPL",
      currentPrice: "150.00",
      marketCap: "2500000000000",
      sector: "Technology",
      historicalVolatility: "25",
      impliedVolatility: "28",
      beta: "1.2",
      vix: "18.5",
      high52Week: "182.00",
      low52Week: "124.00",
      averageTrueRange: "3.50",
      standardDeviation: "18.50",
      positionSize: "10000",
      timeHorizon: "30",
      confidenceLevel: "95",
      marketRegime: "Normal",
      correlationSPY: "0.85",
      earningsDate: "2024-01-25",
      var95: "1250",
      expectedShortfall: "1850",
      maxDrawdown: "15.5",
      sharpeRatio: "1.8"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!volatilityAnalysis) return;
    
    const csvContent = [
      "Metric,Value",
      `Symbol,${inputs.symbol}`,
      `Current Price,$${volatilityAnalysis.price.current}`,
      `Historical Volatility,${volatilityAnalysis.volatility.historical.toFixed(2)}%`,
      `Implied Volatility,${volatilityAnalysis.volatility.implied.toFixed(2)}%`,
      `Beta,${volatilityAnalysis.risk.beta}`,
      `Daily VaR,$${volatilityAnalysis.risk.dailyVaR.toFixed(2)}`,
      `Horizon VaR,$${volatilityAnalysis.risk.horizonVaR.toFixed(2)}`,
      `Expected Shortfall,$${volatilityAnalysis.risk.dailyES.toFixed(2)}`,
      `Max Drawdown,${volatilityAnalysis.risk.maxDrawdown.toFixed(2)}%`,
      `Sharpe Ratio,${volatilityAnalysis.risk.sharpeRatio.toFixed(2)}`,
      `Overall Risk Score,${volatilityAnalysis.metrics.overallRiskScore.toFixed(1)}`,
      `Risk Level,${volatilityAnalysis.metrics.riskLevel.level}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'volatility-risk-analysis.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Analysis exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!volatilityAnalysis) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      analysis: volatilityAnalysis
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Analysis saved to history", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { historicalVolatility: "15", beta: "0.8", maxDrawdown: "8" },
      moderate: { historicalVolatility: "25", beta: "1.2", maxDrawdown: "15" },
      aggressive: { historicalVolatility: "40", beta: "1.6", maxDrawdown: "25" }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} volatility preset applied`, "info");
  };

  const sectorPreset = (sector) => {
    const presets = {
      tech: { historicalVolatility: "30", beta: "1.3", sector: "Technology" },
      healthcare: { historicalVolatility: "25", beta: "0.9", sector: "Healthcare" },
      financial: { historicalVolatility: "22", beta: "1.1", sector: "Financial" },
      energy: { historicalVolatility: "35", beta: "1.4", sector: "Energy" }
    };
    
    setInputs(prev => ({ ...prev, ...presets[sector] }));
    addNotification(`${sector} sector preset applied`, "info");
  };

  // Chart Data
  const volatilityChartData = volatilityAnalysis ? {
    labels: ["Historical", "Implied", "VIX Adjusted", "Daily", "Horizon"],
    datasets: [{
      label: 'Volatility Metrics (%)',
      data: [
        volatilityAnalysis.volatility.historical,
        volatilityAnalysis.volatility.implied,
        volatilityAnalysis.volatility.normalized * 100,
        volatilityAnalysis.volatility.daily,
        volatilityAnalysis.volatility.horizon
      ],
      backgroundColor: [
        'rgba(25, 105, 44, 0.6)',
        'rgba(40, 167, 69, 0.6)',
        'rgba(255, 193, 7, 0.6)',
        'rgba(255, 107, 107, 0.6)',
        'rgba(229, 62, 62, 0.6)'
      ],
      borderColor: '#19692C',
      borderWidth: 2
    }]
  } : null;

  const riskRadarData = volatilityAnalysis ? {
    labels: ['Volatility', 'Market Beta', 'VaR Risk', 'Drawdown', 'Efficiency'],
    datasets: [{
      label: 'Risk Profile',
      data: [
        volatilityAnalysis.metrics.volatilityScore,
        volatilityAnalysis.metrics.betaScore,
        volatilityAnalysis.metrics.varScore,
        volatilityAnalysis.metrics.drawdownScore,
        volatilityAnalysis.metrics.overallRiskScore
      ],
      backgroundColor: 'rgba(25, 105, 44, 0.2)',
      borderColor: '#19692C',
      pointBackgroundColor: '#19692C',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: '#19692C'
    }]
  } : null;

  const probabilityChartData = riskMetrics ? {
    labels: ['+5% Gain', '-5% Loss', '+10% Gain', '-10% Loss', 'Breach Support', 'Breach Resistance'],
    datasets: [{
      label: 'Probability (%)',
      data: [
        riskMetrics.probabilities.up5Percent,
        riskMetrics.probabilities.down5Percent,
        riskMetrics.probabilities.up10Percent,
        riskMetrics.probabilities.down10Percent,
        riskMetrics.probabilities.breachSupport,
        riskMetrics.probabilities.breachResistance
      ],
      backgroundColor: [
        'rgba(40, 167, 69, 0.6)',
        'rgba(255, 107, 107, 0.6)',
        'rgba(40, 167, 69, 0.8)',
        'rgba(255, 107, 107, 0.8)',
        'rgba(255, 193, 7, 0.6)',
        'rgba(255, 193, 7, 0.8)'
      ],
      borderColor: '#19692C',
      borderWidth: 2
    }]
  } : null;

  const riskLevel = volatilityAnalysis ? volatilityAnalysis.metrics.riskLevel : null;

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Stock Volatility & Risk Calculator</h1>
        </div>
        <p>Comprehensive volatility analysis and risk assessment for informed trading decisions</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "analyzer" ? "active" : ""}`}
                onClick={() => setActiveTab("analyzer")}
              >
                <i className="fas fa-chart-line"></i>
                {isMobile ? 'Analyzer' : 'Volatility Analyzer'}
              </button>
              <button 
                className={`tab ${activeTab === "metrics" ? "active" : ""}`}
                onClick={() => setActiveTab("metrics")}
              >
                <i className="fas fa-chart-bar"></i>
                {isMobile ? 'Metrics' : 'Risk Metrics'}
              </button>
              <button 
                className={`tab ${activeTab === "scenarios" ? "active" : ""}`}
                onClick={() => setActiveTab("scenarios")}
              >
                <i className="fas fa-project-diagram"></i>
                {isMobile ? 'Scenarios' : 'Scenario Analysis'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Risk Profile Presets */}
          <div className="presets-section">
            <h4>Risk Profile:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Sector Selection */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${analysisType === "historical" ? "active" : ""}`}
                onClick={() => setAnalysisType("historical")}
              >
                <i className="fas fa-chart-line"></i>
                Historical Analysis
              </button>
              <button 
                className={`mode-btn ${analysisType === "implied" ? "active" : ""}`}
                onClick={() => setAnalysisType("implied")}
              >
                <i className="fas fa-crystal-ball"></i>
                Implied Volatility
              </button>
              <button 
                className={`mode-btn ${analysisType === "stress" ? "active" : ""}`}
                onClick={() => setAnalysisType("stress")}
              >
                <i className="fas fa-bolt"></i>
                Stress Testing
              </button>
            </div>
            <div className="mode-description">
              {analysisType === "historical" 
                ? "Analyze historical price movements and volatility patterns"
                : analysisType === "implied"
                ? "Focus on options-implied volatility and market expectations"
                : "Test portfolio resilience under extreme market conditions"
              }
            </div>
          </div>

          {/* Sector Presets */}
          <div className="presets-section">
            <h4>Sector Presets:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => sectorPreset('tech')}>
                Technology
              </button>
              <button className="preset-btn moderate" onClick={() => sectorPreset('healthcare')}>
                Healthcare
              </button>
              <button className="preset-btn aggressive" onClick={() => sectorPreset('financial')}>
                Financial
              </button>
              <button className="preset-btn aggressive" onClick={() => sectorPreset('energy')}>
                Energy
              </button>
            </div>
          </div>

          {/* Volatility Analyzer Tab */}
          {activeTab === "analyzer" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Stock Information
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-tag"></i> Stock Symbol</label>
                      <input 
                        type="text" 
                        name="symbol" 
                        value={inputs.symbol} 
                        onChange={handleChange}
                        placeholder="AAPL"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Current Price</label>
                      <input 
                        type="number" 
                        name="currentPrice" 
                        value={inputs.currentPrice} 
                        onChange={handleChange}
                        placeholder="150.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-industry"></i> Sector</label>
                      <select name="sector" value={inputs.sector} onChange={handleChange}>
                        <option value="Technology">Technology</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Financial">Financial</option>
                        <option value="Energy">Energy</option>
                        <option value="Consumer">Consumer</option>
                        <option value="Industrial">Industrial</option>
                      </select>
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-wave-square"></i>
                    Volatility Metrics
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Historical Volatility (%)</label>
                      <input 
                        type="number" 
                        name="historicalVolatility" 
                        value={inputs.historicalVolatility} 
                        onChange={handleChange}
                        placeholder="25"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-crystal-ball"></i> Implied Volatility (%)</label>
                      <input 
                        type="number" 
                        name="impliedVolatility" 
                        value={inputs.impliedVolatility} 
                        onChange={handleChange}
                        placeholder="28"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-balance-scale"></i> Beta</label>
                      <input 
                        type="number" 
                        name="beta" 
                        value={inputs.beta} 
                        onChange={handleChange}
                        placeholder="1.2"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-gauge-high"></i> VIX Level</label>
                      <input 
                        type="number" 
                        name="vix" 
                        value={inputs.vix} 
                        onChange={handleChange}
                        placeholder="18.5"
                        step="0.1"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-chart-bar"></i>
                    Price & Range Data
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-up"></i> 52-Week High</label>
                      <input 
                        type="number" 
                        name="high52Week" 
                        value={inputs.high52Week} 
                        onChange={handleChange}
                        placeholder="182.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-down"></i> 52-Week Low</label>
                      <input 
                        type="number" 
                        name="low52Week" 
                        value={inputs.low52Week} 
                        onChange={handleChange}
                        placeholder="124.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrows-left-right"></i> Average True Range</label>
                      <input 
                        type="number" 
                        name="averageTrueRange" 
                        value={inputs.averageTrueRange} 
                        onChange={handleChange}
                        placeholder="3.50"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-area"></i> Standard Deviation</label>
                      <input 
                        type="number" 
                        name="standardDeviation" 
                        value={inputs.standardDeviation} 
                        onChange={handleChange}
                        placeholder="18.50"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save Analysis
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Risk Metrics Tab */}
          {activeTab === "metrics" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-shield-alt"></i>
                  Risk Parameters
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-dollar-sign"></i> Position Size</label>
                    <input 
                      type="number" 
                      name="positionSize" 
                      value={inputs.positionSize} 
                      onChange={handleChange}
                      placeholder="10000"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-clock"></i> Time Horizon (Days)</label>
                    <input 
                      type="number" 
                      name="timeHorizon" 
                      value={inputs.timeHorizon} 
                      onChange={handleChange}
                      placeholder="30"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-percentage"></i> Confidence Level (%)</label>
                    <select name="confidenceLevel" value={inputs.confidenceLevel} onChange={handleChange}>
                      <option value="90">90%</option>
                      <option value="95">95%</option>
                      <option value="99">99%</option>
                      <option value="99.5">99.5%</option>
                    </select>
                  </div>
                </div>

                <h3>
                  <i className="fas fa-cogs"></i>
                  Advanced Risk Metrics
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-calculator"></i> VaR (95%)</label>
                    <input 
                      type="number" 
                      name="var95" 
                      value={inputs.var95} 
                      onChange={handleChange}
                      placeholder="1250"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-chart-line"></i> Expected Shortfall</label>
                    <input 
                      type="number" 
                      name="expectedShortfall" 
                      value={inputs.expectedShortfall} 
                      onChange={handleChange}
                      placeholder="1850"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-arrow-down"></i> Max Drawdown (%)</label>
                    <input 
                      type="number" 
                      name="maxDrawdown" 
                      value={inputs.maxDrawdown} 
                      onChange={handleChange}
                      placeholder="15.5"
                      step="0.1"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-star"></i> Sharpe Ratio</label>
                    <input 
                      type="number" 
                      name="sharpeRatio" 
                      value={inputs.sharpeRatio} 
                      onChange={handleChange}
                      placeholder="1.8"
                      step="0.1"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Analysis History
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No analyses yet. Start analyzing to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className={`history-profit ${calc.analysis.metrics.overallRiskScore <= 50 ? 'positive' : 'negative'}`}>
                          <i className={`fas ${calc.analysis.metrics.overallRiskScore <= 50 ? 'fa-shield' : 'fa-bolt'}`}></i>
                          {calc.analysis.metrics.riskLevel.level}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-chart-line"></i> Vol: {calc.analysis.volatility.historical.toFixed(1)}%</span>
                        <span><i className="fas fa-balance-scale"></i> Beta: {calc.analysis.risk.beta.toFixed(2)}</span>
                        <span><i className="fas fa-exclamation-triangle"></i> Score: {calc.analysis.metrics.overallRiskScore.toFixed(0)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "analyzer" || activeTab === "metrics" || activeTab === "scenarios") && (
          <div className="right-panel">
            {activeTab === "analyzer" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-chart-line"></i>
                    Volatility Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {volatilityAnalysis ? (
                  <>
                    {/* Risk Level Summary */}
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value" style={{ color: '#FFFFFF' }}>
                          {volatilityAnalysis.metrics.overallRiskScore.toFixed(1)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-shield-alt"></i>
                          Overall Risk Score
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value" style={{ color: riskLevel?.color }}>
                          {riskLevel?.level}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-flag"></i>
                          Risk Level
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {volatilityAnalysis.volatility.historical.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-line"></i>
                          Historical Vol
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {volatilityAnalysis.risk.beta.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-balance-scale"></i>
                          Beta
                        </div>
                      </div>
                    </div>

                    {/* Risk Level Indicator */}
                    <div className="risk-summary">
                      <h4>Risk Assessment: <span style={{ color: riskLevel?.color }}>{riskLevel?.level} - {riskLevel?.description}</span></h4>
                      <div className="risk-meter">
                        <div className="meter-bar">
                          <div 
                            className="meter-fill"
                            style={{ 
                              width: `${volatilityAnalysis.metrics.overallRiskScore}%`,
                              backgroundColor: riskLevel?.color
                            }}
                          ></div>
                        </div>
                        <div className="meter-value" style={{ color: riskLevel?.color }}>
                          Score: {volatilityAnalysis.metrics.overallRiskScore.toFixed(1)}/100
                        </div>
                      </div>
                    </div>

                    {/* Volatility Metrics */}
                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-crystal-ball"></i> Implied Volatility:</span>
                        <span>{volatilityAnalysis.volatility.implied.toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-wave-square"></i> Volatility Premium:</span>
                        <span style={{ color: volatilityAnalysis.volatility.premium > 0 ? '#E53E3E' : '#28A745' }}>
                          {volatilityAnalysis.volatility.premium > 0 ? '+' : ''}{volatilityAnalysis.volatility.premium.toFixed(2)}%
                        </span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-gauge-high"></i> VIX Adjusted Vol:</span>
                        <span>{volatilityAnalysis.volatility.normalized.toFixed(2)}x</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-arrows-left-right"></i> Daily Trading Range:</span>
                        <span>${volatilityAnalysis.price.atr} (±{volatilityAnalysis.price.atrPercent.toFixed(2)}%)</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-link"></i> Market Correlation:</span>
                        <span>{(volatilityAnalysis.risk.correlation * 100).toFixed(1)}%</span>
                      </div>
                    </div>

                    {/* Price Position */}
                    <div className="risk-summary">
                      <h4>Price Position in 52-Week Range</h4>
                      <div className="risk-metrics">
                        <div className="risk-metric">
                          <span className="metric-label">Distance to High:</span>
                          <span className="metric-value" style={{ color: '#E53E3E' }}>
                            -{volatilityAnalysis.price.currentToHigh.toFixed(1)}%
                          </span>
                        </div>
                        <div className="risk-metric">
                          <span className="metric-label">Distance to Low:</span>
                          <span className="metric-value" style={{ color: '#28A745' }}>
                            +{volatilityAnalysis.price.currentToLow.toFixed(1)}%
                          </span>
                        </div>
                        <div className="risk-metric">
                          <span className="metric-label">Range Position:</span>
                          <span className="metric-value">
                            {(volatilityAnalysis.price.position * 100).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Volatility Comparison Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-bar"></i>
                        Volatility Metrics Comparison
                      </h3>
                      <Bar 
                        data={volatilityChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: false
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: {
                                display: true,
                                text: 'Percentage (%)'
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Risk Radar Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-bullseye"></i>
                        Risk Profile Radar
                      </h3>
                      <Radar 
                        data={riskRadarData}
                        options={{
                          responsive: true,
                          scales: {
                            r: {
                              beginAtZero: true,
                              max: 100,
                              ticks: {
                                stepSize: 20
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-chart-line fa-3x"></i>
                    <p>Enter stock details to analyze volatility and risk</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "metrics" && volatilityAnalysis && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-chart-bar"></i>
                  Advanced Risk Metrics
                </h3>

                {/* Value at Risk Metrics */}
                <div className="projection-summary">
                  <div className="summary-card">
                    <div className="summary-value" style={{ color: '#FF6B6B' }}>
                      ${volatilityAnalysis.risk.dailyVaR.toFixed(0)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-exclamation-triangle"></i>
                      Daily VaR ({inputs.confidenceLevel}%)
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value" style={{ color: '#E53E3E' }}>
                      ${volatilityAnalysis.risk.horizonVaR.toFixed(0)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-calendar"></i>
                      {inputs.timeHorizon}-Day VaR
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value" style={{ color: '#FF6B6B' }}>
                      ${volatilityAnalysis.risk.dailyES.toFixed(0)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-chart-line-down"></i>
                      Expected Shortfall
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value" style={{ color: '#28A745' }}>
                      {volatilityAnalysis.risk.sharpeRatio.toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-star"></i>
                      Sharpe Ratio
                    </div>
                  </div>
                </div>

                {/* Risk-Adjusted Metrics */}
                <div className="risk-summary">
                  <h4>Risk-Adjusted Performance</h4>
                  <div className="risk-metrics">
                    <div className="risk-metric">
                      <span className="metric-label">Volatility per Return Unit:</span>
                      <span className="metric-value">
                        {(volatilityAnalysis.volatility.historical / volatilityAnalysis.risk.sharpeRatio).toFixed(2)}%
                      </span>
                    </div>
                    <div className="risk-metric">
                      <span className="metric-label">Beta-Adjusted Volatility:</span>
                      <span className="metric-value">
                        {volatilityAnalysis.risk.betaAdjustedVol.toFixed(2)}%
                      </span>
                    </div>
                    <div className="risk-metric">
                      <span className="metric-label">Market-Adjusted VaR:</span>
                      <span className="metric-value">
                        ${volatilityAnalysis.risk.marketAdjustedVaR.toFixed(0)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Recommendations */}
                <div className="risk-summary">
                  <h4>Risk Management Recommendations</h4>
                  <div className="scenario-list">
                    {volatilityAnalysis.recommendations.map((rec, index) => (
                      <div key={index} className="scenario-item">
                        <div className="scenario-name">
                          <i className={`fas fa-${rec.type === 'success' ? 'check' : rec.type === 'warning' ? 'exclamation-triangle' : 'exclamation-circle'} fa-xs`}></i>
                          {rec.message}
                        </div>
                        <div className="scenario-outcome">{rec.action}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "scenarios" && riskMetrics && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-project-diagram"></i>
                  Scenario & Probability Analysis
                </h3>
                
                {/* Probability Analysis */}
                <div className="chart-container">
                  <h3>
                    <i className="fas fa-chart-bar"></i>
                    Price Movement Probabilities
                  </h3>
                  <Bar 
                    data={probabilityChartData}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: {
                          display: false
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Probability (%)'
                          }
                        }
                      }
                    }}
                  />
                </div>

                {/* Stress Test Results */}
                <div className="scenario-comparison">
                  <h4>Stress Test Scenarios</h4>
                  {Object.entries(riskMetrics.stressTests).map(([scenario, loss]) => (
                    <div key={scenario} className="scenario-item">
                      <div className="scenario-name">
                        {scenario.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </div>
                      <div className="scenario-stats">
                        <div className="breakdown-item">
                          <span>Potential Loss:</span>
                          <span style={{ color: '#E53E3E' }}>${Math.abs(loss).toFixed(0)}</span>
                        </div>
                        <div className="breakdown-item">
                          <span>Loss Percentage:</span>
                          <span style={{ color: '#E53E3E' }}>
                            {((Math.abs(loss) / parseFloat(inputs.positionSize)) * 100).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Scenario Analysis */}
                <div className="risk-summary">
                  <h4>Scenario Analysis</h4>
                  <div className="risk-metrics">
                    {riskMetrics.scenarioAnalysis.map((scenario, index) => (
                      <div key={index} className="risk-metric">
                        <span className="metric-label">{scenario.name} ({scenario.probability}%):</span>
                        <span className="metric-value" style={{ 
                          color: scenario.return >= 0 ? '#28A745' : '#E53E3E'
                        }}>
                          {scenario.return >= 0 ? '+' : ''}{scenario.return.toFixed(1)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default StockVolatilityRiskCalculator;